import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FCM } from '@capacitor-community/fcm';
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Badge } from '@capawesome/capacitor-badge';
import { AlertService } from './alert.service';
import { User } from './auth.service';
import { selectDomain, selectIsConnected, selectUser } from 'src/app/store/selectors/auth.selectors';
import { setDomain, updateDevice } from 'src/app/store/actions/auth.actions';
import { filter, take } from 'rxjs/operators';
import { Domain } from 'src/app/services/domain.service';
import { ReadTrackingService } from './read-tracking.service';

@Injectable({
  providedIn: 'root',
})
export class FcmService {
  public fcmToken: string | null = null;
  private user: User;
  private pendingNotificationData: any = null;
  private lastDeviceSyncAt = 0;
  private readonly minDeviceSyncIntervalMs = 2500;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private store: Store,
    private alertService: AlertService,
    private readTrackingService: ReadTrackingService
  ) {
    if (Capacitor.isNativePlatform()) {

      PushNotifications.addListener('registration', async ({ value }) => {
        let token = value; // Push token for Android

        // Get FCM token instead the APN one returned by Capacitor
        if (Capacitor.getPlatform() === 'ios') {
          const { token: fcm_token } = await FCM.getToken();
          token = fcm_token;
        }
        const previousToken = this.fcmToken;
        console.log('FCM token', token);
        this.fcmToken = token || null;
        if (this.fcmToken && this.fcmToken !== previousToken) {
          this.requestDeviceSync('registration');
        }
      });

      PushNotifications.addListener('registrationError', (error: any) => {
        console.error('FCM registration error', error);
      });

      PushNotifications.addListener(
        'pushNotificationReceived',
        ({ title, body, data }: PushNotificationSchema) => {
          if (data?.article) {
            this.alertService.push(body, {
              icon: 'file-text',
              route: `article/${data.article}`,
              title,
              data,
            });
          }
          if (data?.chat) {
            this.alertService.push(body, {
              icon: 'message-circle',
              route: `chat/${data.chat}`,
              title,
              data,
            });
          }
          if (data?.survey) {
            this.alertService.push(body, {
              icon: 'megaphone',
              route: `sondage/${data.survey}`,
              title,
              data,
            });
          }
          if (data?.faq) {
            const faqRouteQueryParams = this.buildFaqRouteQueryParams(data);
            this.alertService.push(body, {
              icon: 'help-circle',
              route: `faq/${data.faq}`,
              routeQueryParams: faqRouteQueryParams,
              title,
              data,
            });
          }
        }
      );

      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        ({ notification }: ActionPerformed) => {
          const data = notification.data;
          this.processNotificationAction(data);
        }
      );

      window.addEventListener('pushNotificationActionPerformed', (event: any) => {
        const data = event.detail.notification;
        this.processNotificationAction(data);
      });

      this.store
        .select(selectUser)
        .pipe(filter((user: User | null): user is User => !!user))
        .subscribe((user: User) => {
          this.user = user;
          if (this.pendingNotificationData) {
            this.handleNotificationAction(this.pendingNotificationData);
            this.pendingNotificationData = null;
          }
        });
    }
  }

  private processNotificationAction(data: any) {
    if (this.user) {
      this.handleNotificationAction(data);
    } else {
      this.pendingNotificationData = data;
    }
  }


  private handleNotificationAction(data: any) {
    const { domain } = data;

    this.switchDomainIfNeeded(domain)
      .then(() => {
        this.ngZone.run(() => {
          Badge.clear();
          this.handleNavigation(data);
        });
      })
      .catch((error) => {
        console.error('Domain switch failed:', error);
      });
  }


  private switchDomainIfNeeded(domainUuid: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (!domainUuid) {
          return resolve();
        }
  
        this.store
          .select(selectDomain)
          .pipe(
            filter((currentDomain: Domain | null): currentDomain is Domain => !!currentDomain),
            take(1)
          )
          .subscribe(
            (currentDomain: Domain) => {
              if (currentDomain.uuid !== domainUuid) {
                if (this.user && this.user.domains) {
                  const targetDomain = this.user.domains.find(
                    (d: Domain) => d.uuid === domainUuid
                  );
                  if (targetDomain) {
                    this.store.dispatch(setDomain({ domain: targetDomain }));
                    this.store
                      .select(selectDomain)
                      .pipe(
                        filter(
                          (domain: Domain | null): domain is Domain =>
                            !!domain && domain.uuid === domainUuid
                        ),
                        take(1)
                      )
                      .subscribe(
                        () => {
                          resolve();
                        },
                        (error) => {
                          reject(error);
                        }
                      );
                  } else {
                    reject(new Error('Access to the domain is denied.'));
                  }
                } else {
                  reject(new Error('User domains not available.'));
                }
              } else {
                resolve();
              }
            },
            (error) => {
              reject(error);
            }
          );
      } catch (error) {
        reject(error);
      }
    });
  }  

  handleNavigation(data: any) {
    console.log('FCM Navigation - received data:', data);
    
    if (data?.article) {
      this.readTrackingService.markNotificationReboundPending();
      console.log('Navigating to article:', data.article);
      this.router.navigate([`article/${data.article}`], {
        queryParams: this.readTrackingService.withNotificationSource(),
      });
    } else if (data?.faq) {
      this.readTrackingService.markNotificationReboundPending();
      const faqRouteQueryParams = this.buildFaqRouteQueryParams(data);
      console.log('Navigating to FAQ:', data.faq);
      this.router.navigate([`faq/${data.faq}`], {
        queryParams: faqRouteQueryParams,
      });
    } else if (data?.chat) {
      console.log('Navigating to chat:', data.chat);
      this.router.navigate([`chat/${data.chat}`]);
    } else if (data?.survey) {
      console.log('Navigating to survey:', data.survey);
      setTimeout(() => {
        this.router.navigate([`sondage/${data.survey}`]);
      }, 1000);
    } else if (data?.surveygroup) {
      console.log('Navigating to surveygroup:', data.surveygroup);
      setTimeout(() => {
        this.router.navigate([`sondage/${data.surveygroup}`]);
      }, 1000);
    } else {
      console.log('No matching navigation found for data:', data);
    }
}

  private buildFaqRouteQueryParams(data: any): Record<string, string> {
    const faqUuids = this.extractFaqUuids(data);
    return this.readTrackingService.withNotificationSource({
      fromNotification: 'true',
      notificationFaqUuids: faqUuids.length > 1 ? faqUuids.join(',') : undefined,
    }) as Record<string, string>;
  }

  private extractFaqUuids(data: any): string[] {
    const collected: string[] = [];

    const pushIfValid = (value: any) => {
      if (typeof value !== 'string') {
        return;
      }

      const trimmed = value.trim();
      if (trimmed !== '') {
        collected.push(trimmed);
      }
    };

    pushIfValid(data?.faq);
    this.parseFaqListSource(data?.faqs).forEach(pushIfValid);
    this.parseFaqListSource(data?.faqsUuids).forEach(pushIfValid);
    this.parseFaqListSource(data?.faq_uuids).forEach(pushIfValid);
    this.parseFaqListSource(data?.faqUuids).forEach(pushIfValid);

    return Array.from(new Set(collected));
  }

  private parseFaqListSource(source: unknown): string[] {
    if (Array.isArray(source)) {
      return source.map((value) => String(value));
    }

    if (typeof source !== 'string') {
      return [];
    }

    const trimmed = source.trim();
    if (trimmed === '') {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((value) => String(value));
        }
      } catch (error) {
        // Keep the fallback split for malformed payloads.
      }
    }

    return trimmed
      .split(/[;,]/)
      .map((value) => value.trim())
      .filter((value) => value !== '');
  }

  /**
   * Registers the device for push notifications.
   *
   * The permission is only requested when the user has never been asked
   * (`prompt`) and `askPermission` is true. On Android 13+ `requestPermissions()`
   * opens a system activity that pauses and resumes the app, which fires
   * `appStateChange` again: asking after a refusal therefore looped the app in
   * and out of the permission screen several times per second, closing the
   * keyboard, blocking copy/paste and eventually killing the app.
   */
  register(askPermission: boolean = true): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    PushNotifications.checkPermissions().then(({ receive }) => {
      if (receive === 'granted') {
        return PushNotifications.register();
      }
      if (receive === 'prompt' && askPermission) {
        return PushNotifications.requestPermissions().then((permission) => {
          if (permission.receive === 'granted') {
            return PushNotifications.register();
          }
          console.warn('Notifications permission not granted:', permission.receive);
        });
      }
      console.warn('Notifications permission not requested:', receive);
    });
  }

  syncTokenWithBackendIfConnected(reason: string = 'manual'): void {
    this.requestDeviceSync(reason);
  }

  async subscribe(topic: string) {
    await this.executeTopicOperation('subscribe', topic, async () => {
      await FCM.subscribeTo({ topic });
    });
  }

  async unsubscribe(topic: string) {
    await this.executeTopicOperation('unsubscribe', topic, async () => {
      await FCM.unsubscribeFrom({ topic });
    });
  }

  async syncDomainTopics(
    domainUuid: string,
    notifications: { news?: boolean; surveys?: boolean; faq?: boolean } | null | undefined,
  ): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    if (!domainUuid || !notifications) {
      return;
    }

    const operations: Array<Promise<void>> = [];
    const map: Array<{ key: 'news' | 'surveys' | 'faq'; prefix: string }> = [
      { key: 'news', prefix: 'news' },
      { key: 'surveys', prefix: 'surveys' },
      { key: 'faq', prefix: 'faq' },
    ];

    map.forEach(({ key, prefix }) => {
      const topic = `${prefix}-${domainUuid}`;
      if (notifications[key]) {
        operations.push(this.subscribe(topic));
      } else {
        operations.push(this.unsubscribe(topic));
      }
    });

    await Promise.all(operations);
  }

  async getOrFetchFcmToken(timeoutMs: number = 5000): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    if (this.fcmToken) {
      return this.fcmToken;
    }

    try {
      await PushNotifications.register();
    } catch (error) {
      // Registration errors are handled by the listener.
    }

    try {
      const { token } = await FCM.getToken();
      if (token) {
        this.fcmToken = token;
        return token;
      }
    } catch (error) {
      // Keep waiting for push registration listener below.
    }

    const startedAt = Date.now();
    while (!this.fcmToken && (Date.now() - startedAt) < timeoutMs) {
      await this.sleep(250);
    }

    return this.fcmToken ?? null;
  }

  async getToken() {
    const token = await this.getOrFetchFcmToken();
    if (!token) {
      throw new Error('No FCM token available');
    }
  }

  getFcmToken(): string | null {
    return this.fcmToken;
  }

  private async executeTopicOperation(
    operation: 'subscribe' | 'unsubscribe',
    topic: string,
    action: () => Promise<void>,
  ): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const token = await this.getOrFetchFcmToken();
    if (!token) {
      console.warn(`[FCM] ${operation} skipped for ${topic}: missing token`);
      return;
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await action();
        return;
      } catch (error) {
        if (attempt === maxAttempts) {
          console.error(`[FCM] ${operation} failed for ${topic}`, error);
          return;
        }
        await this.sleep(300 * attempt);
      }
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private requestDeviceSync(reason: string): void {
    const now = Date.now();
    if (now - this.lastDeviceSyncAt < this.minDeviceSyncIntervalMs) {
      return;
    }

    this.store
      .select(selectIsConnected)
      .pipe(take(1))
      .subscribe((isConnected: boolean) => {
        if (!isConnected) {
          return;
        }
        this.lastDeviceSyncAt = Date.now();
        this.store.dispatch(updateDevice());
      });
  }
}
